"use client";

import { motion } from "framer-motion";
import {
	Box,
	Check,
	ChevronUp,
	Leaf,
	Minus,
	Package,
	Plus,
	RefreshCw,
	Search,
	ShoppingCart,
	UtensilsCrossed,
	X,
} from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { StockFilter } from "@/components/build-a-box/box-filters";
import {
	SortSelect,
	StockFilterSelect,
} from "@/components/build-a-box/box-filters";
import {
	filterProductsByTags,
	TagFilterSelect,
} from "@/components/build-a-box/tag-filter-select";

const AddToCartDialog = dynamic(
	() =>
		import("@/components/cart/add-to-cart-dialog").then(
			(m) => m.AddToCartDialog,
		),
	{ ssr: false },
);

const StockLimitDialog = dynamic(
	() =>
		import("@/components/cart/stock-limit-dialog").then(
			(m) => m.StockLimitDialog,
		),
	{ ssr: false },
);

import { useCart } from "@/components/cart/cart-context";
import { LoadingSpinnerDialog } from "@/components/cart/loading-spinner-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import {
	BOX_TIERS,
	FREQUENCY_OPTIONS,
	fromCents,
	getFrequencyLabel,
	MINIMUM_BOX_SIZE,
	previewBundleDiscount,
	toCents,
} from "@/lib/appstle";
import {
	getMetafieldValue,
	METAFIELD_CONFIGS,
	parseMetafieldValue,
	productMatchesMetafieldFilter,
} from "@/lib/shopify/metafield-utils";
import type { Product, ProductVariant, SellingPlan } from "@/lib/shopify/types";
import { formatPrice } from "@/lib/shopify/utils";
import {
	buildStockLimitMessage,
	parseStockLimitMessage,
} from "@/lib/stock-message-helpers";
import {
	getProductStockLimit,
	useBuildBoxStore,
} from "@/lib/store/build-box-store";
import { cn } from "@/lib/utils";

const ProductDetailDialog = dynamic(
	() =>
		import("@/components/products/product-detail-dialog").then(
			(m) => m.ProductDetailDialog,
		),
	{ ssr: false },
);

interface BuildABoxClientProps {
	products: Product[];
	/** True when the server failed to fetch from Shopify (cache missed + API error) */
	serverFailed?: boolean;
	pageTitle?: string;
	pageDescription?: string;
	refreshPath?: string;
	steps?: Array<{ label: string; desc: string }>;
}

const DEFAULT_STEPS = [
	{ label: "1. Size", desc: "12-80 meals" },
	{ label: "2. Pick", desc: "Mix & match" },
	{ label: "3. Ship", desc: "We Pack and Ship" },
];

const gridVariants = {
	hidden: { opacity: 0 },
	show: { opacity: 1, transition: { staggerChildren: 0.03 } },
} as const;

const cardVariants = {
	hidden: { opacity: 0, y: 15 },
	show: { opacity: 1, y: 0, transition: { duration: 0.25 } },
} as const;

const MEAT_TAGS = [
	"Pork",
	"Turkey",
	"Chicken",
	"Elk",
	"Beef",
	"Bison",
	"Salmon",
	"Shrimp",
	"Fish",
	"Lamb",
	"Venison",
	"Duck",
] as const;
const TYPE_TAGS = ["Full Meal", "Meat Only"] as const;
const MEAT_SET = new Set(MEAT_TAGS.map((t) => t.toLowerCase()));
const TYPE_SET = new Set(TYPE_TAGS.map((t) => t.toLowerCase()));

interface ProductCardProps {
	product: Product;
	quantity: number;
	availableStockLimit: number | null;
	canAddMore: boolean;
	remainingSlots: number;
	index: number;
	onAddToBox: (product: Product) => void;
	onRemoveFromBox: (product: Product) => void;
	onQuantityChange: (product: Product, value: string) => void;
	onViewDetail: (product: Product) => void;
}

const ProductCard = memo(function ProductCard({
	product,
	quantity,
	availableStockLimit,
	canAddMore,
	remainingSlots,
	index,
	onAddToBox,
	onRemoveFromBox,
	onQuantityChange,
	onViewDetail,
}: ProductCardProps) {
	const image = product.images.edges[0]?.node;
	const secondImage = product.images.edges[1]?.node;
	const price = product.priceRange.minVariantPrice;
	const compareAtPrice = product.compareAtPriceRange?.minVariantPrice;
	const currentPriceAmount = parseFloat(price.amount);
	const compareAtPriceAmount = compareAtPrice
		? parseFloat(compareAtPrice.amount)
		: 0;
	const hasDiscount =
		compareAtPriceAmount > currentPriceAmount && compareAtPriceAmount > 0;
	const discountPercentage = hasDiscount
		? Math.round((1 - currentPriceAmount / compareAtPriceAmount) * 100)
		: 0;
	const isNew = product.tags?.some((tag) => tag.toLowerCase() === "new");
	const isShopifyOutOfStock =
		!product.availableForSale ||
		(product.variants[0] && product.variants[0].availableForSale === false);
	const stockLimit = availableStockLimit;
	const isOutOfStock = isShopifyOutOfStock || stockLimit === 0;
	const maxSelectableQuantity =
		stockLimit === null
			? remainingSlots + quantity
			: Math.min(remainingSlots + quantity, stockLimit);
	const stockRemaining =
		stockLimit === null ? null : Math.max(stockLimit - quantity, 0);
	const canIncreaseQuantity =
		canAddMore &&
		!isOutOfStock &&
		(stockLimit === null || quantity < stockLimit);

	const dietaryVals = (product.tags ?? []).filter(
		(tag) =>
			!MEAT_SET.has(tag.toLowerCase()) && !TYPE_SET.has(tag.toLowerCase()),
	);
	const meatVals = (product.tags ?? []).filter((tag) =>
		MEAT_SET.has(tag.toLowerCase()),
	);
	const typeTagVals = (product.tags ?? []).filter((tag) =>
		TYPE_SET.has(tag.toLowerCase()),
	);
	const formVals = parseMetafieldValue(
		getMetafieldValue(product, "custom", "food_product_form"),
	);
	const formAll = Array.from(new Set([...formVals, ...typeTagVals]));
	const rawBadges = [
		...formAll.map((v) => ({ label: v, type: "form" as const })),
		...meatVals.map((v) => ({ label: v, type: "meat" as const })),
		...dietaryVals.map((v) => ({ label: v, type: "dietary" as const })),
	];
	const allBadges = rawBadges.slice(0, 4);
	const hiddenBadgeCount = rawBadges.length - allBadges.length;

	return (
		<motion.div
			variants={cardVariants}
			className={cn(
				"group relative flex flex-col rounded-xl bg-card border-2 transition-all overflow-hidden",
				quantity > 0
					? "border-primary shadow-lg ring-2 ring-primary/20"
					: "border-border hover:border-foreground/30 shadow-sm hover:shadow-md",
			)}
			role="listitem"
		>
			<button
				type="button"
				className="cursor-pointer text-left w-full h-full p-0 border-none bg-transparent"
				onClick={() => onViewDetail(product)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onViewDetail(product);
					}
				}}
				aria-label={`View details for ${product.title}, ${formatPrice(price.amount, price.currencyCode)}${isOutOfStock ? ", out of stock" : ""}`}
			>
				<div className="relative aspect-4/3 bg-secondary overflow-hidden">
					{image ? (
						<>
							<Image
								src={image.url || "/placeholder.svg"}
								alt={image.altText || product.title}
								fill
								priority={index < 4}
								className={cn(
									"object-cover transition-all duration-400 ease-out",
									secondImage
										? "group-hover:opacity-0 group-hover:scale-105"
										: "group-hover:scale-105",
								)}
								sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
							/>
							{secondImage && (
								<Image
									src={secondImage.url || "/placeholder.svg"}
									alt={
										secondImage.altText || `${product.title} - alternate view`
									}
									fill
									className="object-contain bg-white dark:bg-background transition-all duration-400 ease-out opacity-0 group-hover:opacity-100"
									sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
								/>
							)}
						</>
					) : (
						<div
							className="flex h-full items-center justify-center text-muted-foreground text-xs"
							aria-hidden="true"
						>
							No image
						</div>
					)}

					{quantity > 0 && (
						<output
							className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground shadow-lg"
							aria-label={`${quantity} in box`}
						>
							{quantity}
						</output>
					)}

					<div className="absolute top-2 left-2 flex flex-col gap-1 items-start max-w-[80%]">
						{hasDiscount && (
							<Badge className="bg-red-500 hover:bg-red-600 text-white font-bold text-[10px] px-1.5 py-0.5 whitespace-nowrap shadow-sm">
								-{discountPercentage}% OFF
							</Badge>
						)}
						{isNew && (
							<Badge className="bg-blue-500 hover:bg-blue-600 text-white font-bold text-[10px] px-1.5 py-0.5 whitespace-nowrap shadow-sm">
								NEW
							</Badge>
						)}
						{!isOutOfStock &&
							stockRemaining !== null &&
							stockRemaining > 0 &&
							stockRemaining <= 10 && (
								<Badge className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-[10px] px-1.5 py-0.5 whitespace-nowrap shadow-sm">
									Only {stockRemaining} left
								</Badge>
							)}
					</div>

					{isOutOfStock && (
						<div className="absolute inset-0 flex items-center justify-center bg-black/50">
							<span className="bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
								Out of Stock
							</span>
						</div>
					)}
				</div>

				<div className="flex flex-1 flex-col p-3">
					<p className="text-xs sm:text-sm font-semibold text-foreground line-clamp-2 mb-1">
						{product.title}
					</p>

					{allBadges.length > 0 && (
						<div className="flex flex-wrap gap-1 mb-1">
							{allBadges.map((badge) => (
								<span
									key={`${badge.type}-${badge.label}`}
									className={cn(
										"inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap",
										badge.type === "dietary" &&
											"bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
										badge.type === "form" &&
											"bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
										badge.type === "meat" &&
											"bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
									)}
								>
									{badge.type === "dietary" && (
										<Leaf className="w-2.5 h-2.5" aria-hidden="true" />
									)}
									{badge.type === "form" && (
										<Package className="w-2.5 h-2.5" aria-hidden="true" />
									)}
									{badge.type === "meat" && (
										<UtensilsCrossed
											className="w-2.5 h-2.5"
											aria-hidden="true"
										/>
									)}
									{badge.label}
								</span>
							))}
							{hiddenBadgeCount > 0 && (
								<span className="inline-flex items-center text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
									+{hiddenBadgeCount}
								</span>
							)}
						</div>
					)}

					<div className="flex flex-wrap items-center gap-1 mt-auto">
						{hasDiscount && compareAtPrice && (
							<span className="text-[10px] sm:text-xs text-muted-foreground line-through">
								{formatPrice(
									compareAtPrice.amount,
									compareAtPrice.currencyCode,
								)}
							</span>
						)}
						<span
							className={cn(
								"font-bold text-xs sm:text-sm",
								hasDiscount ? "text-red-600 dark:text-red-500" : "text-primary",
							)}
						>
							{formatPrice(price.amount, price.currencyCode)}
						</span>
						{hasDiscount && (
							<span className="text-[9px] sm:text-[10px] text-red-600 dark:text-red-500 font-medium">
								Save{" "}
								{formatPrice(
									(compareAtPriceAmount - currentPriceAmount).toFixed(2),
									price.currencyCode,
								)}
							</span>
						)}
					</div>
				</div>
			</button>

			<div className="px-3 pb-3">
				<div className="pt-2 border-t border-border/50">
					{isOutOfStock ? (
						<Button
							size="sm"
							className="w-full rounded-full font-bold h-9 text-xs"
							disabled
							aria-label={`${product.title} is out of stock`}
						>
							Out of Stock
						</Button>
					) : quantity > 0 ? (
						<fieldset
							className="flex items-center gap-1.5 border-0 p-0 m-0 min-w-0"
							aria-label={`Quantity controls for ${product.title}`}
						>
							<Button
								variant="outline"
								size="icon"
								className="h-8 w-8 rounded-full"
								onClick={(e) => {
									e.stopPropagation();
									onRemoveFromBox(product);
								}}
								aria-label={`Remove one ${product.title}`}
							>
								<Minus className="h-3.5 w-3.5" aria-hidden="true" />
							</Button>
							<Input
								type="number"
								min={0}
								max={maxSelectableQuantity}
								value={quantity}
								onClick={(e) => e.stopPropagation()}
								onChange={(e) => onQuantityChange(product, e.target.value)}
								aria-label={`Quantity of ${product.title}`}
								className="flex-1 h-8 text-center font-bold text-sm bg-transparent border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
							/>
							<Button
								variant="outline"
								size="icon"
								className="h-8 w-8 rounded-full"
								onClick={(e) => {
									e.stopPropagation();
									onAddToBox(product);
								}}
								disabled={!canIncreaseQuantity}
								aria-label={`Add one more ${product.title}`}
							>
								<Plus className="h-3.5 w-3.5" aria-hidden="true" />
							</Button>
						</fieldset>
					) : (
						<Button
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								onAddToBox(product);
							}}
							className="w-full rounded-full font-bold h-9 text-xs"
							disabled={!canIncreaseQuantity}
							aria-label={`Add ${product.title} to box`}
						>
							Add to Box
						</Button>
					)}
				</div>
			</div>
		</motion.div>
	);
});

const BoxSummaryContent = dynamic(
	() => import("./box-summary-content").then((mod) => mod.BoxSummaryContent),
	{
		loading: () => <Skeleton className="h-100 w-full rounded-xl" />,
		ssr: false,
	},
);

export function BuildABoxClient({
	products: serverProducts,
	serverFailed = false,
	pageTitle = "Build Your Box",
	pageDescription,
	refreshPath = "/build-a-box",
	steps = DEFAULT_STEPS,
}: BuildABoxClientProps) {
	const isMobile = useIsMobile();
	const { toast } = useToast();
	const { addItems, cart } = useCart();
	const searchParams = useSearchParams();
	const [showMobileSheet, setShowMobileSheet] = useState(false);
	const [subscriptionFrequency, setSubscriptionFrequency] = useState("weekly");
	const [showAddToCartDialog, setShowAddToCartDialog] = useState(false);
	const [showStockIssueDialog, setShowStockIssueDialog] = useState(false);
	const [stockIssueMessage, setStockIssueMessage] = useState("");
	const [stockIssueMeals, setStockIssueMeals] = useState<string[]>([]);
	const [isAddingToCart, setIsAddingToCart] = useState(false);
	const [detailProduct, setDetailProduct] = useState<Product | null>(null);

	const {
		selectedItems,
		targetSize,
		purchaseType,
		setTargetSize,
		setPurchaseType,
		addToBox,
		removeFromBox,
		setItemQuantity,
		resetBox,
		cachedProducts,
		setCachedProducts,
		// Filter state from zustand
		searchQuery,
		categoryFilter,
		sortBy,
		dietTagFilter,
		meatTagFilter,
		typeTagFilter,
		// Filter actions
		setSearchQuery,
		setSortBy,
		setDietTagFilter,
		setMeatTagFilter,
		setTypeTagFilter,
		resetFilters,
	} = useBuildBoxStore();

	// Client-side fallback: use Zustand cached products when server returns nothing
	const products = useMemo(
		() => (serverProducts.length > 0 ? serverProducts : cachedProducts),
		[serverProducts, cachedProducts],
	);

	// True when the server had an error but we have cached products to show
	const isUsingCache =
		serverFailed && serverProducts.length === 0 && products.length > 0;

	const router = useRouter();

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				router.refresh();
			}
		};
		window.addEventListener("focus", handleVisibilityChange);
		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			window.removeEventListener("focus", handleVisibilityChange);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [router]);

	useEffect(() => {
		if (serverProducts.length > 0) {
			setCachedProducts(serverProducts);
		}
	}, [serverProducts, setCachedProducts]);

	useEffect(() => {
		const sizeParam = searchParams.get("size") || searchParams.get("box");
		if (sizeParam) {
			const size = Number.parseInt(sizeParam, 10);
			if (
				!Number.isNaN(size) &&
				BOX_TIERS.some((tier) => tier.count === size)
			) {
				setTargetSize(size);
			}
		}
	}, [searchParams, setTargetSize]);

	const searchInputRef = useRef<HTMLInputElement>(null);

	const [_mobileStep, setMobileStep] = useState<
		"purchase-type" | "box-size" | "meals" | "review"
	>("purchase-type");
	const [hasSelectedPurchaseType, setHasSelectedPurchaseType] = useState(true);
	const [_hasSelectedBoxSize, setHasSelectedBoxSize] = useState(false);
	const [stockFilter, setStockFilter] = useState<StockFilter>("all");

	const hasActiveFilters =
		stockFilter !== "all" ||
		searchQuery !== "" ||
		categoryFilter !== "all" ||
		Object.keys(dietTagFilter).length > 0 ||
		Object.keys(meatTagFilter).length > 0 ||
		Object.keys(typeTagFilter).length > 0;

	const clearAllFilters = useCallback(() => {
		resetFilters();
		setStockFilter("all");
	}, [resetFilters]);

	const openStockIssueDialog = useCallback(
		(message: string, meals: string[] = []) => {
			const uniqueMeals = Array.from(
				new Set(meals.map((meal) => meal.trim()).filter(Boolean)),
			);
			setStockIssueMessage(message);
			setStockIssueMeals(uniqueMeals);
			setShowStockIssueDialog(true);
		},
		[],
	);

	const _categoryOptions = useMemo(() => {
		const options = new Set<string>();
		for (const product of products) {
			if (product.category?.name) options.add(product.category.name);
			for (const v of parseMetafieldValue(
				getMetafieldValue(
					product,
					METAFIELD_CONFIGS.category.namespace,
					METAFIELD_CONFIGS.category.key,
				),
			)) {
				options.add(v);
			}
		}
		return Array.from(options).sort();
	}, [products]);

	const { dietTagOptions, meatTagOptions, typeTagOptions } = useMemo(() => {
		const meatSet = new Set(MEAT_TAGS.map((t) => t.toLowerCase()));
		const typeSet = new Set(TYPE_TAGS.map((t) => t.toLowerCase()));

		// Case-insensitive dedup: lowercase key -> first-seen canonical casing
		const diet = new Map<string, string>();
		const meat = new Map<string, string>();
		const type = new Map<string, string>();

		for (const product of products) {
			for (const tag of product.tags ?? []) {
				const lower = tag.toLowerCase();
				if (meatSet.has(lower)) {
					if (!meat.has(lower)) meat.set(lower, tag);
				} else if (typeSet.has(lower)) {
					if (!type.has(lower)) type.set(lower, tag);
				} else {
					if (!diet.has(lower)) diet.set(lower, tag);
				}
			}
		}

		return {
			dietTagOptions: Array.from(diet.values()).sort(),
			meatTagOptions: Array.from(meat.values()).sort(),
			typeTagOptions: Array.from(type.values()).sort(),
		};
	}, [products]);

	const inStockProducts = useMemo(
		() =>
			products.filter(
				(p) =>
					p.availableForSale &&
					(typeof p.totalInventory !== "number" || p.totalInventory > 0),
			),
		[products],
	);

	const outOfStockProducts = useMemo(
		() =>
			products.filter(
				(p) =>
					!p.availableForSale ||
					(typeof p.totalInventory === "number" && p.totalInventory <= 0),
			),
		[products],
	);

	const filteredProducts = useMemo(() => {
		const base =
			stockFilter === "in-stock"
				? inStockProducts
				: stockFilter === "out-of-stock"
					? outOfStockProducts
					: products;
		let result = base;

		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			result = result.filter(
				(p) =>
					p.title.toLowerCase().includes(query) ||
					p.description?.toLowerCase().includes(query),
			);
		}

		if (categoryFilter !== "all") {
			result = result.filter((p) => {
				if (p.category?.name === categoryFilter) return true;
				return productMatchesMetafieldFilter(
					p,
					METAFIELD_CONFIGS.category,
					categoryFilter,
				);
			});
		}

		if (Object.keys(dietTagFilter).length > 0) {
			result = filterProductsByTags(result, dietTagFilter);
		}

		if (Object.keys(meatTagFilter).length > 0) {
			result = filterProductsByTags(result, meatTagFilter);
		}

		if (Object.keys(typeTagFilter).length > 0) {
			result = filterProductsByTags(result, typeTagFilter);
		}

		if (sortBy !== "best-selling") {
			result = [...result];
			switch (sortBy) {
				case "price-low":
					result.sort(
						(a, b) =>
							parseFloat(a.priceRange.minVariantPrice.amount) -
							parseFloat(b.priceRange.minVariantPrice.amount),
					);
					break;
				case "price-high":
					result.sort(
						(a, b) =>
							parseFloat(b.priceRange.minVariantPrice.amount) -
							parseFloat(a.priceRange.minVariantPrice.amount),
					);
					break;
				case "name-asc":
					result.sort((a, b) => a.title.localeCompare(b.title));
					break;
				case "name-desc":
					result.sort((a, b) => b.title.localeCompare(a.title));
					break;
			}
		}

		return result;
	}, [
		stockFilter,
		inStockProducts,
		outOfStockProducts,
		products,
		searchQuery,
		categoryFilter,
		dietTagFilter,
		meatTagFilter,
		typeTagFilter,
		sortBy,
	]);

	const cartVariantQuantities = useMemo(() => {
		const quantities = new Map<string, number>();
		for (const edge of cart?.lines.edges || []) {
			if (edge.node.id.startsWith("temp-")) continue;
			const variantId = edge.node.merchandise.id;
			quantities.set(
				variantId,
				(quantities.get(variantId) ?? 0) + edge.node.quantity,
			);
		}
		return quantities;
	}, [cart]);

	const getAvailableStockLimit = useCallback(
		(product: Product) => {
			const stockLimit = getProductStockLimit(product);
			if (stockLimit === null) return null;

			const variantId = product.variants[0]?.id;
			if (!variantId) return stockLimit;

			const inCartQuantity = cartVariantQuantities.get(variantId) ?? 0;
			return Math.max(stockLimit - inCartQuantity, 0);
		},
		[cartVariantQuantities],
	);

	const totalMeals = useMemo(
		() =>
			Array.from(selectedItems.values()).reduce(
				(sum, item) => sum + item.quantity,
				0,
			),
		[selectedItems],
	);

	const totalPrice = useMemo(
		() =>
			Array.from(selectedItems.values()).reduce((sum, item) => {
				const price = parseFloat(
					item.product.priceRange.minVariantPrice.amount,
				);
				return sum + price * item.quantity;
			}, 0),
		[selectedItems],
	);

	const currentTier = BOX_TIERS.find((tier) => tier.count === targetSize);
	const currentDiscount =
		purchaseType === "subscribe" && currentTier
			? currentTier.subscriptionDiscount
			: 0;

	const boxPricePreview = useMemo(() => {
		let subtotalCents = 0;
		let discountCents = 0;
		for (const item of selectedItems.values()) {
			const unitCents = toCents(
				parseFloat(item.product.priceRange.minVariantPrice.amount) || 0,
			);
			subtotalCents += unitCents * item.quantity;
			if (currentDiscount > 0) {
				const unitDiscountCents = previewBundleDiscount(
					unitCents,
					"PERCENTAGE",
					currentDiscount,
				).discountCents;
				discountCents += unitDiscountCents * item.quantity;
			}
		}
		return {
			subtotalCents,
			discountCents,
			totalCents: subtotalCents - discountCents,
		};
	}, [selectedItems, currentDiscount]);

	const discountedPrice = fromCents(boxPricePreview.totalCents);
	const savingsAmount = fromCents(boxPricePreview.discountCents);

	const progressPercentage = Math.min((totalMeals / targetSize) * 100, 100);
	const isBoxComplete = totalMeals >= targetSize;
	const canAddMore = totalMeals < targetSize;
	const remainingSlots = targetSize - totalMeals;

	useEffect(() => {
		if (!isMobile) setShowMobileSheet(false);
	}, [isMobile]);

	useEffect(() => {
		if (isBoxComplete && totalMeals > 0 && isMobile) {
			setShowMobileSheet(true);
			setMobileStep("review");
		}
	}, [isBoxComplete, totalMeals, isMobile]);

	const handleMobilePurchaseTypeSelect = useCallback(
		(type: "onetime" | "subscribe") => {
			setPurchaseType(type);
			setHasSelectedPurchaseType(true);
			setMobileStep("box-size");
		},
		[setPurchaseType],
	);

	const handleSizeChange = useCallback(
		(newSize: number) => {
			setTargetSize(newSize);
			setHasSelectedBoxSize(true);
			setMobileStep("meals");
			if (totalMeals > newSize) {
				toast({
					title: "Box Updated",
					description: `Your selection was trimmed to fit the ${newSize}-meal box.`,
				});
			}
		},
		[setTargetSize, totalMeals, toast],
	);

	const handleAddToBox = useCallback(
		(product: Product) => {
			if (!canAddMore) {
				toast({
					title: "Box Full",
					description: `Your ${targetSize}-meal box is full. Remove items or select a larger size.`,
					variant: "destructive",
				});
				return;
			}

			const stockLimit = getAvailableStockLimit(product);
			const currentQuantity = selectedItems.get(product.id)?.quantity ?? 0;
			if (stockLimit !== null && currentQuantity >= stockLimit) {
				toast({
					title: "Stock Limit Reached",
					description:
						stockLimit > 0
							? `Current available stock limit reached for ${product.title}.`
							: `${product.title} is currently unavailable for additional boxes.`,
					variant: "destructive",
				});
				return;
			}

			addToBox(product);
		},
		[
			canAddMore,
			targetSize,
			selectedItems,
			addToBox,
			toast,
			getAvailableStockLimit,
		],
	);

	const handleRemoveFromBox = useCallback(
		(product: Product) => {
			removeFromBox(product);
		},
		[removeFromBox],
	);

	const handleManualQuantity = useCallback(
		(product: Product, value: string) => {
			const num = Number.parseInt(value, 10);
			if (Number.isNaN(num) || num < 0) return;
			if (num === 0) {
				removeFromBox(product, true);
				return;
			}

			const currentQuantity = selectedItems.get(product.id)?.quantity ?? 0;
			const maxByBoxSize = targetSize - (totalMeals - currentQuantity);
			const stockLimit = getAvailableStockLimit(product);
			const maxAllowed =
				stockLimit === null ? maxByBoxSize : Math.min(maxByBoxSize, stockLimit);

			if (maxAllowed <= 0) {
				removeFromBox(product, true);
				toast({
					title: "Cannot Add More",
					description:
						stockLimit === 0
							? `${product.title} is currently out of stock.`
							: `Your ${targetSize}-meal box is full.`,
					variant: "destructive",
				});
				return;
			}

			const clampedQuantity = Math.min(num, maxAllowed);
			if (clampedQuantity !== num) {
				toast({
					title: "Quantity Adjusted",
					description:
						stockLimit !== null && maxAllowed === stockLimit
							? `${product.title} has only ${stockLimit} in stock.`
							: `Your box can only fit ${maxAllowed} of ${product.title}.`,
					variant: "destructive",
				});
			}

			setItemQuantity(product, clampedQuantity);
		},
		[
			removeFromBox,
			selectedItems,
			targetSize,
			totalMeals,
			setItemQuantity,
			toast,
			getAvailableStockLimit,
		],
	);

	const handleViewDetail = useCallback((product: Product) => {
		setDetailProduct(product);
	}, []);

	const handleCloseDetail = useCallback(() => {
		setDetailProduct(null);
	}, []);

	const findSellingPlanId = useCallback(
		(
			product: Product,
			frequency: string,
			targetBoxSize: number,
		): string | undefined => {
			if (!product.sellingPlanGroups?.nodes?.length) return undefined;

			const frequencyOption = FREQUENCY_OPTIONS.find(
				(f) => f.value === frequency,
			);
			if (!frequencyOption) return undefined;

			const targetSizeStr = targetBoxSize.toString();
			const tierTokens = BOX_TIERS.map((tier) => tier.count.toString());
			const hasExactNumberToken = (value: string, token: string) =>
				new RegExp(`(^|[^0-9])${token}([^0-9]|$)`).test(value);
			const hasAnyTierToken = (value: string) =>
				tierTokens.some((token) => hasExactNumberToken(value, token));

			const matchesFrequency = (planName: string) => {
				if (frequencyOption.interval !== "WEEK") return false;

				if (frequencyOption.intervalCount === 1) {
					return (
						/week/.test(planName) &&
						!/(^|[^0-9])2\s*-?\s*week/.test(planName) &&
						!/(^|[^0-9])4\s*-?\s*week/.test(planName)
					);
				}

				if (frequencyOption.intervalCount === 2) {
					return /(^|[^0-9])2\s*-?\s*week/.test(planName);
				}

				if (frequencyOption.intervalCount === 4) {
					return /(^|[^0-9])4\s*-?\s*week/.test(planName);
				}

				return false;
			};

			let genericFrequencyPlanId: string | undefined;
			let tierFrequencyPlanId: string | undefined;

			// First pass: exact frequency + exact box-size token.
			for (const group of product.sellingPlanGroups.nodes) {
				const groupName = group.name?.toLowerCase() || "";
				const plans: SellingPlan[] = group.sellingPlans?.nodes || [];

				for (const plan of plans) {
					const planName = plan.name.toLowerCase();

					if (
						matchesFrequency(planName) &&
						(hasExactNumberToken(planName, targetSizeStr) ||
							hasExactNumberToken(groupName, targetSizeStr))
					) {
						return plan.id;
					}

					if (matchesFrequency(planName) && !genericFrequencyPlanId) {
						const hasTierHint =
							hasAnyTierToken(planName) || hasAnyTierToken(groupName);
						if (!hasTierHint) {
							genericFrequencyPlanId = plan.id;
						} else if (!tierFrequencyPlanId) {
							tierFrequencyPlanId = plan.id;
						}
					}
				}
			}

			// Final fallback keeps subscription enabled when products have plans.
			if (genericFrequencyPlanId || tierFrequencyPlanId) {
				return genericFrequencyPlanId ?? tierFrequencyPlanId;
			}

			for (const group of product.sellingPlanGroups.nodes) {
				const fallback = group.sellingPlans?.nodes?.[0]?.id;
				if (fallback) return fallback;
			}

			return undefined;
		},
		[],
	);

	const isAddingToCartRef = useRef(false);

	const handleAddAllToCart = useCallback(async () => {
		if (isAddingToCartRef.current) return;

		if (totalMeals < MINIMUM_BOX_SIZE) {
			toast({
				title: "Minimum Not Met",
				description: `You need at least ${MINIMUM_BOX_SIZE} meals in your box.`,
				variant: "destructive",
			});
			return;
		}

		if (totalMeals < targetSize) {
			toast({
				title: "Box Not Complete",
				description: `Please add ${targetSize - totalMeals} more meals to complete your box.`,
				variant: "destructive",
			});
			return;
		}

		isAddingToCartRef.current = true;
		setIsAddingToCart(true);

		try {
			const existingVariantQuantities = new Map<string, number>();
			for (const edge of cart?.lines.edges || []) {
				if (edge.node.id.startsWith("temp-")) continue;
				const variantId = edge.node.merchandise.id;
				existingVariantQuantities.set(
					variantId,
					(existingVariantQuantities.get(variantId) ?? 0) + edge.node.quantity,
				);
			}

			const limitedMealDetails: string[] = [];

			for (const item of selectedItems.values()) {
				const variant = item.product.variants[0];
				if (!variant) continue;

				const quantityAvailable = variant.quantityAvailable;
				if (typeof quantityAvailable !== "number") continue;

				const existingQty = existingVariantQuantities.get(variant.id) ?? 0;
				const remainingQty = quantityAvailable - existingQty;

				if (remainingQty <= 0) {
					limitedMealDetails.push(
						buildStockLimitMessage(item.product.title, existingQty, 0),
					);
					continue;
				}

				if (item.quantity > remainingQty) {
					limitedMealDetails.push(
						buildStockLimitMessage(
							item.product.title,
							existingQty,
							remainingQty,
						),
					);
				}
			}

			if (limitedMealDetails.length > 0) {
				const firstMeal = limitedMealDetails[0]?.split(" (")[0];
				openStockIssueDialog(
					limitedMealDetails.length === 1 && firstMeal
						? `${firstMeal} is currently stock-limited due to high demand. Please choose another meal for now. We are restocking soon.`
						: "Some selected meals are currently stock-limited due to high demand. Please choose alternative meals for now. We are restocking soon.",
					limitedMealDetails,
				);
				return;
			}

			const boxId =
				typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
					? `box_${crypto.randomUUID()}`
					: `box_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
			const effectiveBoxDiscount =
				purchaseType === "subscribe" ? currentDiscount : 0;
			const boxAttributes = [
				{ key: "_box_id", value: boxId },
				{ key: "box_id", value: boxId },
				{ key: "_box_size", value: targetSize.toString() },
				{ key: "box_size", value: targetSize.toString() },
				{ key: "_purchase_type", value: purchaseType },
				{ key: "purchase_type", value: purchaseType },
				{ key: "_box_discount", value: effectiveBoxDiscount.toString() },
				{ key: "box_discount", value: effectiveBoxDiscount.toString() },
				{
					key: "_frequency",
					value: purchaseType === "subscribe" ? subscriptionFrequency : "",
				},
				{
					key: "frequency",
					value: purchaseType === "subscribe" ? subscriptionFrequency : "",
				},
			];

			const itemsToAdd: Array<{
				variant: ProductVariant;
				product: Product;
				quantity: number;
				attributes: { key: string; value: string }[];
				sellingPlanId?: string;
			}> = [];

			for (const item of selectedItems.values()) {
				const variant = item.product.variants[0];
				if (variant) {
					const boxItemId = `${boxId}_item_${variant.id}`;
					const itemAttributes = [
						...boxAttributes,
						{ key: "_box_item_id", value: boxItemId },
						{ key: "box_item_id", value: boxItemId },
						{ key: "_box_item_product_id", value: item.product.id },
						{ key: "_box_item_variant_id", value: variant.id },
					];

					const sellingPlanId =
						purchaseType === "subscribe"
							? findSellingPlanId(
									item.product,
									subscriptionFrequency,
									targetSize,
								) ||
								item.product.sellingPlanGroups?.nodes?.[0]?.sellingPlans
									?.nodes?.[0]?.id
							: undefined;

					itemsToAdd.push({
						variant,
						product: item.product,
						quantity: item.quantity,
						attributes: itemAttributes,
						sellingPlanId,
					});
				}
			}

			const addResult = await addItems(itemsToAdd);
			if (!addResult.success) {
				const enrichedLimitedMeals = (addResult.limitedMeals ?? []).map(
					(limitedMeal) => {
						const limitedMatch = limitedMeal.match(
							/^(.*?)\s*\(\s*(?:currently\s*)?(\d+)\s*available\s*\)$/i,
						);
						if (!limitedMatch) {
							const parsed = parseStockLimitMessage(limitedMeal);
							if (!parsed) return limitedMeal;

							const selectedMeal = Array.from(selectedItems.values()).find(
								(item) =>
									item.product.title.trim().toLowerCase() ===
									parsed.mealTitle.trim().toLowerCase(),
							);

							const variantId = selectedMeal?.product.variants[0]?.id;
							const inCartQuantity = variantId
								? (existingVariantQuantities.get(variantId) ?? 0)
								: parsed.inCartQuantity;

							return buildStockLimitMessage(
								selectedMeal?.product.title ?? parsed.mealTitle,
								inCartQuantity,
								parsed.availableQuantity,
							);
						}

						const mealTitle = limitedMatch[1]?.trim();
						const availableCount = Number.parseInt(limitedMatch[2] ?? "", 10);
						if (!mealTitle || Number.isNaN(availableCount)) return limitedMeal;

						const selectedMeal = Array.from(selectedItems.values()).find(
							(item) =>
								item.product.title.trim().toLowerCase() ===
								mealTitle.toLowerCase(),
						);
						const variantId = selectedMeal?.product.variants[0]?.id;
						const inCartQuantity = variantId
							? (existingVariantQuantities.get(variantId) ?? 0)
							: 0;

						return buildStockLimitMessage(
							selectedMeal?.product.title ?? mealTitle,
							inCartQuantity,
							availableCount,
						);
					},
				);

				if ((addResult.limitedMeals?.length ?? 0) > 0) {
					for (const limitedMeal of enrichedLimitedMeals) {
						const parsedLimitedMeal = parseStockLimitMessage(limitedMeal);
						if (!parsedLimitedMeal) continue;

						const mealTitle = parsedLimitedMeal.mealTitle?.trim().toLowerCase();
						const availableCount = parsedLimitedMeal.availableQuantity;
						if (!mealTitle || Number.isNaN(availableCount)) continue;

						const selectedMeal = Array.from(selectedItems.values()).find(
							(item) => item.product.title.trim().toLowerCase() === mealTitle,
						);
						if (!selectedMeal) continue;

						setItemQuantity(
							selectedMeal.product,
							Math.min(selectedMeal.quantity, availableCount),
						);
					}
				}

				openStockIssueDialog(
					(addResult.limitedMeals?.length ?? 0) > 0
						? "Some selected meals are currently stock-limited due to high demand. Please choose alternative meals for now. We are restocking soon."
						: addResult.message ||
								"Could not add the full box because one or more meals are unavailable.",
					enrichedLimitedMeals,
				);
				return;
			}

			setShowMobileSheet(false);
			setShowAddToCartDialog(true);
			resetBox();
		} catch (error) {
			const msg =
				error instanceof Error
					? error.message
					: "Failed to add box to cart. Please try again.";
			openStockIssueDialog(msg);
		} finally {
			setIsAddingToCart(false);
			isAddingToCartRef.current = false;
		}
	}, [
		cart,
		totalMeals,
		targetSize,
		purchaseType,
		currentDiscount,
		subscriptionFrequency,
		selectedItems,
		setItemQuantity,
		addItems,
		toast,
		findSellingPlanId,
		resetBox,
		openStockIssueDialog,
	]);

	if (products.length === 0) {
		return (
			<div
				className="text-center py-20 animate-in fade-in duration-500"
				role="alert"
			>
				<div className="max-w-md mx-auto space-y-6">
					<div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
						<Package
							className="w-10 h-10 text-destructive/60"
							aria-hidden="true"
						/>
					</div>
					<div className="space-y-2">
						<h3 className="text-xl font-semibold">
							{serverFailed
								? "Oops! Unable to Load Products"
								: "No Products Available"}
						</h3>
						<p className="text-muted-foreground">
							{serverFailed
								? "We\u2019re having trouble connecting to our store right now. Please try refreshing the page a few times — it usually resolves quickly."
								: "There are no products in this collection right now. Check back soon!"}
						</p>
					</div>
					{serverFailed && (
						<div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive/80">
							If refreshing doesn&apos;t help, our team has been notified and is
							working on it. Try again in a few minutes.
						</div>
					)}
					<div className="flex flex-col sm:flex-row items-center justify-center gap-3">
						<Button variant="outline" className="gap-2" asChild>
							<Link href={refreshPath}>
								<RefreshCw className="w-4 h-4" aria-hidden="true" />
								Refresh Page
							</Link>
						</Button>
						<Button variant="ghost" className="gap-2" asChild>
							<Link href="/">Back to Home</Link>
						</Button>
					</div>
				</div>
			</div>
		);
	}

	const summaryProps = {
		totalMeals,
		targetSize,
		isBoxComplete,
		remainingSlots,
		progressPercentage,
		selectedItems,
		removeFromBox,
		handleManualQuantity,
		handleAddToBox,
		canAddMore,
		purchaseType,
		setPurchaseType,
		subscriptionFrequency,
		setSubscriptionFrequency,
		totalPrice,
		currentDiscount,
		savingsAmount,
		discountedPrice,
		handleAddAllToCart,
		existingCartVariantQuantities: cartVariantQuantities,
	};

	return (
		<div className="max-w-450 mx-auto px-4 pb-36 sm:pb-32 lg:pb-12 animate-in fade-in duration-300">
			{isUsingCache && (
				<output
					className="mb-4 flex items-start gap-3 rounded-xl border border-amber-400/60 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-500/40 px-4 py-3 text-amber-800 dark:text-amber-300"
					aria-live="polite"
				>
					<RefreshCw
						className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
						aria-hidden="true"
					/>
					<div className="flex-1 min-w-0">
						<p className="text-sm font-semibold">
							Showing previously loaded products
						</p>
						<p className="text-xs mt-0.5 text-amber-700 dark:text-amber-400">
							We couldn&apos;t reach our store right now. Displaying your last
							loaded menu &mdash;{" "}
							<Link
								href={refreshPath}
								className="underline underline-offset-2 font-medium hover:opacity-80"
							>
								refresh to try again
							</Link>
						</p>
					</div>
				</output>
			)}

			<header className="text-center py-8 sm:py-12 md:py-16 space-y-3">
				<h1 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tight uppercase">
					{pageTitle}
				</h1>
				{pageDescription && (
					<p className="mx-auto max-w-3xl text-sm text-muted-foreground sm:text-base">
						{pageDescription}
					</p>
				)}

				<nav
					className="flex justify-center gap-4 sm:gap-8 mt-8 overflow-x-auto pb-2 no-scrollbar"
					aria-label="Build a box steps"
				>
					{steps.map((step, index) => {
						const stepIcons = [Box, Check, Package];
						const StepIcon = stepIcons[index] ?? Package;

						return (
							<div
								key={step.label}
								className="flex flex-col items-center gap-2 min-w-20"
							>
								<div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
									<StepIcon className="w-5 h-5" aria-hidden="true" />
								</div>
								<div className="text-center">
									<p className="font-bold text-sm">{step.label}</p>
									<p className="text-xs text-muted-foreground">{step.desc}</p>
								</div>
							</div>
						);
					})}
				</nav>
			</header>

			<div className="grid gap-6 lg:gap-10 lg:grid-cols-12 items-start">
				<div className="lg:col-span-8 space-y-8">
					<section
						className="lg:hidden"
						aria-labelledby="purchase-type-heading"
					>
						<div className="flex items-center gap-2 mb-4">
							<span
								className={cn(
									"flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs",
									hasSelectedPurchaseType
										? "bg-emerald-500 text-white"
										: "bg-primary text-primary-foreground",
								)}
								aria-hidden="true"
							>
								{hasSelectedPurchaseType ? (
									<Check className="w-3.5 h-3.5" />
								) : (
									"0"
								)}
							</span>
							<h2
								id="purchase-type-heading"
								className="text-lg sm:text-xl font-bold"
							>
								Choose Purchase Type
							</h2>
						</div>
						<div className="bg-muted/30 p-1 rounded-xl border border-border/50">
							<Button
								type="button"
								variant="ghost"
								aria-pressed={purchaseType === "subscribe"}
								onClick={() => handleMobilePurchaseTypeSelect("subscribe")}
								className={cn(
									"w-full h-auto justify-start flex items-center gap-3 p-3 rounded-lg transition-all border text-left cursor-pointer hover:bg-background/50",
									purchaseType === "subscribe"
										? "bg-primary/5 border-primary shadow-sm hover:bg-primary/5"
										: "border-transparent text-muted-foreground",
								)}
							>
								<div
									className={cn(
										"w-4 h-4 rounded-full border flex items-center justify-center shrink-0",
										purchaseType === "subscribe"
											? "border-primary bg-primary"
											: "border-muted-foreground",
									)}
									aria-hidden="true"
								>
									{purchaseType === "subscribe" && (
										<div className="w-1.5 h-1.5 rounded-full bg-white" />
									)}
								</div>
								<div className="flex-1 whitespace-normal">
									<span className="font-bold text-sm flex items-center gap-2">
										Subscribe & Save
										<Badge
											variant="default"
											className="text-[10px] h-5 bg-emerald-500 hover:bg-emerald-600"
										>
											BEST VALUE
										</Badge>
									</span>
									<span className="text-xs text-emerald-600 font-medium">
										{purchaseType === "subscribe"
											? `Great! You are saving ${currentTier?.subscriptionDiscount ?? 0}%`
											: "Save up to 13%"}
									</span>
									<span className="text-[10px] text-primary font-semibold mt-0.5 block">
										Most customers choose this
									</span>
								</div>
							</Button>

							<Button
								type="button"
								variant="ghost"
								aria-pressed={purchaseType === "onetime"}
								onClick={() => handleMobilePurchaseTypeSelect("onetime")}
								className={cn(
									"w-full h-auto justify-start flex items-center gap-3 p-3 mt-1 rounded-lg transition-all border text-left cursor-pointer hover:bg-background/50",
									purchaseType === "onetime"
										? "bg-background shadow-sm border-border hover:bg-background"
										: "border-transparent text-muted-foreground",
								)}
							>
								<div
									className={cn(
										"w-4 h-4 rounded-full border flex items-center justify-center shrink-0",
										purchaseType === "onetime"
											? "border-primary bg-primary"
											: "border-muted-foreground",
									)}
									aria-hidden="true"
								>
									{purchaseType === "onetime" && (
										<div className="w-1.5 h-1.5 rounded-full bg-white" />
									)}
								</div>
								<div className="flex-1 whitespace-normal">
									<span className="font-bold text-sm block">
										One-time Purchase
									</span>
									<span className="text-xs text-muted-foreground">
										No commitment required
									</span>
								</div>
							</Button>
						</div>
						{hasSelectedPurchaseType && purchaseType === "subscribe" && (
							<div className="mt-3 px-3">
								<Select
									value={subscriptionFrequency}
									onValueChange={setSubscriptionFrequency}
								>
									<SelectTrigger
										className="w-full bg-background h-9 text-xs font-medium border-primary/20"
										aria-label="Delivery frequency"
									>
										<SelectValue placeholder="Delivery Frequency" />
									</SelectTrigger>
									<SelectContent>
										{FREQUENCY_OPTIONS.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-[10px] text-muted-foreground mt-1.5">
									Auto-ships{" "}
									{getFrequencyLabel(subscriptionFrequency).toLowerCase()}.
									Pause or cancel anytime.
								</p>
							</div>
						)}
					</section>
					<section aria-labelledby="box-size-heading">
						<div className="flex items-center gap-2 mb-4">
							<span
								className={cn(
									"flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs",
									currentTier
										? "bg-emerald-500 text-white"
										: "bg-primary text-primary-foreground",
								)}
								aria-hidden="true"
							>
								{currentTier ? <Check className="w-3.5 h-3.5" /> : "1"}
							</span>
							<h2
								id="box-size-heading"
								className="text-lg sm:text-xl font-bold"
							>
								Select Your Box Size
							</h2>
						</div>
						<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
							{BOX_TIERS.map((tier) => {
								const isSelected = currentTier?.count === tier.count;
								return (
									<Button
										key={tier.count}
										type="button"
										variant="ghost"
										aria-pressed={isSelected}
										aria-label={`${tier.count} meals box`}
										onClick={() => handleSizeChange(tier.count)}
										className={cn(
											"group relative h-auto flex flex-col items-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer hover:bg-background/50",
											isSelected
												? "border-primary bg-primary/5 shadow-md scale-[1.02] hover:bg-primary/5"
												: "border-border hover:border-primary/50 hover:shadow-sm",
										)}
									>
										{tier.count === 40 && (
											<div className="absolute top-0 right-0 bg-black text-white dark:bg-white dark:text-black text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-bl-xl rounded-tr-lg shadow-sm whitespace-nowrap z-10 border-l border-b border-border/20">
												BEST
											</div>
										)}
										<Package
											className={cn(
												"w-6 h-6 sm:w-7 sm:h-7 mb-1",
												isSelected
													? "text-primary"
													: "text-muted-foreground group-hover:text-primary/70",
											)}
											aria-hidden="true"
										/>
										<span className="text-3xl font-black">{tier.count}</span>
										<span className="text-[10px] text-muted-foreground uppercase tracking-wide">
											Meals
										</span>
										{tier.subscriptionDiscount > 0 &&
											purchaseType === "subscribe" && (
												<Badge
													variant="secondary"
													className="text-[9px] h-4 mt-1.5 bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
												>
													-{tier.subscriptionDiscount}%
												</Badge>
											)}
									</Button>
								);
							})}
						</div>
					</section>
					{currentTier && (
						<section aria-labelledby="meal-selection-heading">
							<div className="flex items-center gap-2 mb-4">
								<span
									className={cn(
										"flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs",
										totalMeals >= currentTier.count
											? "bg-emerald-500 text-white"
											: "bg-primary text-primary-foreground",
									)}
									aria-hidden="true"
								>
									{totalMeals >= currentTier.count ? (
										<Check className="w-3.5 h-3.5" />
									) : (
										"2"
									)}
								</span>
								<h2
									id="meal-selection-heading"
									className="text-lg sm:text-xl font-bold"
								>
									Choose Your Meals
								</h2>
							</div>

							<div className="space-y-3 mb-6">
								<div className="relative">
									<Search
										className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
										aria-hidden="true"
									/>
									<Input
										ref={searchInputRef}
										type="text"
										placeholder="Search meals..."
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										className="pl-10 h-10"
										aria-label="Search meals"
									/>
									{searchQuery && (
										<button
											type="button"
											onClick={() => setSearchQuery("")}
											className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
											aria-label="Clear search"
										>
											<X className="w-4 h-4" aria-hidden="true" />
										</button>
									)}
								</div>

								<div className="flex flex-wrap gap-2">
									<TagFilterSelect
										tags={dietTagOptions}
										value={dietTagFilter}
										onChange={setDietTagFilter}
										placeholder="Diet"
									/>
									<TagFilterSelect
										tags={meatTagOptions}
										value={meatTagFilter}
										onChange={setMeatTagFilter}
										placeholder="Meat"
									/>
									<TagFilterSelect
										tags={typeTagOptions}
										value={typeTagFilter}
										onChange={setTypeTagFilter}
										placeholder="Type"
									/>
									<SortSelect value={sortBy} onValueChange={setSortBy} />
									<StockFilterSelect
										value={stockFilter}
										onChange={setStockFilter}
									/>
								</div>
							</div>

							<div className="flex items-center justify-between mb-4">
								<p className="text-sm text-muted-foreground" aria-live="polite">
									Showing{" "}
									<span className="font-bold text-foreground">
										{filteredProducts.length}
									</span>{" "}
									of{" "}
									{stockFilter === "in-stock"
										? inStockProducts.length
										: stockFilter === "out-of-stock"
											? outOfStockProducts.length
											: products.length}{" "}
									meals
								</p>
								{hasActiveFilters && (
									<button
										type="button"
										onClick={clearAllFilters}
										className="text-xs text-primary hover:underline font-medium cursor-pointer"
									>
										Clear all filters
									</button>
								)}
							</div>
							{filteredProducts.length > 0 ? (
								<motion.div
									className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4"
									variants={gridVariants}
									initial="hidden"
									animate="show"
									key={`grid-${categoryFilter}-${sortBy}-${JSON.stringify(dietTagFilter)}-${JSON.stringify(meatTagFilter)}-${JSON.stringify(typeTagFilter)}-${stockFilter}-${searchQuery}`}
									role="list"
									aria-label="Available meals"
								>
									{filteredProducts.map((product, index) => {
										const qty = selectedItems.get(product.id)?.quantity ?? 0;
										return (
											<ProductCard
												key={product.id}
												product={product}
												index={index}
												quantity={qty}
												availableStockLimit={getAvailableStockLimit(product)}
												canAddMore={canAddMore}
												remainingSlots={remainingSlots}
												onAddToBox={handleAddToBox}
												onRemoveFromBox={handleRemoveFromBox}
												onQuantityChange={handleManualQuantity}
												onViewDetail={handleViewDetail}
											/>
										);
									})}
								</motion.div>
							) : (
								<div className="text-center py-16">
									<Package
										className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4"
										aria-hidden="true"
									/>
									<p className="text-lg font-semibold">No meals found</p>
									<p className="text-sm text-muted-foreground mt-1">
										Try adjusting your filters
									</p>
									<button
										type="button"
										onClick={clearAllFilters}
										className="mt-3 text-sm text-primary hover:underline font-medium cursor-pointer"
									>
										Clear all filters
									</button>
								</div>
							)}
						</section>
					)}
				</div>
				<aside
					className="hidden lg:block lg:col-span-4 sticky top-24 self-start"
					aria-label="Box summary"
				>
					<BoxSummaryContent {...summaryProps} />
				</aside>
			</div>

			{!showMobileSheet && (
				<div
					className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-background/95 backdrop-blur-xl border-t border-border/50 shadow-2xl safe-area-pb"
					role="toolbar"
					aria-label="Box actions"
				>
					<div className="max-w-lg mx-auto px-4 py-3">
						<div className="flex items-center justify-between gap-3">
							<button
								type="button"
								onClick={() => setShowMobileSheet(true)}
								className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer"
								aria-label={`View box: ${totalMeals} meals, $${totalPrice.toFixed(2)}`}
							>
								<div className="relative shrink-0">
									<div
										className={cn(
											"w-10 h-10 rounded-xl flex items-center justify-center",
											totalMeals > 0
												? "bg-primary text-primary-foreground"
												: "bg-muted text-muted-foreground",
										)}
									>
										<ShoppingCart className="w-5 h-5" aria-hidden="true" />
									</div>
									{totalMeals > 0 && (
										<span className="absolute -top-1.5 -right-1.5 bg-emerald-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
											{totalMeals}
										</span>
									)}
								</div>
								<div className="min-w-0">
									<p className="text-sm font-bold truncate flex items-center gap-1.5">
										{currentTier
											? `${totalMeals} / ${currentTier.count} meals `
											: "Select a size"}
										<ChevronUp className="w-4 h-4 text-primary animate-bounce pt-1" />
									</p>
									{currentTier && (
										<p className="text-xs text-muted-foreground flex items-center gap-1">
											${totalPrice.toFixed(2)} &middot; View Summary
										</p>
									)}
								</div>
							</button>
							<Button
								size="sm"
								className="h-10 px-4 font-bold uppercase text-xs shrink-0"
								disabled={
									!currentTier ||
									totalMeals < currentTier.count ||
									isAddingToCart
								}
								onClick={handleAddAllToCart}
								aria-busy={isAddingToCart}
							>
								{isAddingToCart ? (
									<>
										<RefreshCw
											className="w-3.5 h-3.5 mr-1.5 animate-spin"
											aria-hidden="true"
										/>
										Adding...
									</>
								) : (
									<>
										<ShoppingCart
											className="w-3.5 h-3.5 mr-1.5"
											aria-hidden="true"
										/>
										Add to Cart
									</>
								)}
							</Button>
						</div>
						{currentTier && (
							<div className="mt-2">
								<div className="flex justify-between text-[10px] text-muted-foreground mb-1">
									<span>
										{totalMeals} of {currentTier.count} meals
									</span>
									<span>
										{Math.min(
											Math.round((totalMeals / currentTier.count) * 100),
											100,
										)}
										%
									</span>
								</div>
								<Progress
									value={(totalMeals / currentTier.count) * 100}
									className={cn(
										"h-1 rounded-full overflow-hidden",
										totalMeals >= currentTier.count
											? "**:data-[slot=progress-indicator]:bg-emerald-500"
											: "**:data-[slot=progress-indicator]:bg-primary",
									)}
									aria-label="Box fill progress"
								/>
							</div>
						)}
					</div>
				</div>
			)}
			<Sheet open={showMobileSheet} onOpenChange={setShowMobileSheet}>
				<SheetContent
					side="bottom"
					className="h-[92dvh] max-h-[92dvh] rounded-t-2xl p-0 lg:hidden flex flex-col"
					aria-label="Box summary sheet"
				>
					<SheetHeader className="p-4 pb-2 flex-row items-center justify-between border-b border-border/50 bg-background/50 backdrop-blur-sm sticky top-0 z-10 shrink-0">
						<div className="space-y-1">
							<SheetTitle>Your Box Summary</SheetTitle>
							<SheetDescription className="sr-only">
								Review your current box selection of {totalMeals} meals. Total:
								${totalPrice.toFixed(2)}.
							</SheetDescription>
						</div>
						<DialogClose className="rounded-full bg-secondary p-2 opacity-70 hover:opacity-100 transition-opacity">
							<X className="h-4 w-4" aria-hidden="true" />
							<span className="sr-only">Close</span>
						</DialogClose>
					</SheetHeader>
					<div className="flex-1 overflow-y-auto overscroll-contain p-4 pb-16 safe-area-pb space-y-4">
						<BoxSummaryContent {...summaryProps} />
					</div>
				</SheetContent>
			</Sheet>

			{detailProduct && (
				<ProductDetailDialog
					product={detailProduct}
					onClose={handleCloseDetail}
				/>
			)}

			{showStockIssueDialog && (
				<StockLimitDialog
					open={showStockIssueDialog}
					onOpenChange={setShowStockIssueDialog}
					message={stockIssueMessage}
					selectedMeals={stockIssueMeals}
				/>
			)}

			<LoadingSpinnerDialog
				open={isAddingToCart}
				title="Adding box to cart..."
				description="Checking stock and preparing your box."
			/>

			{showAddToCartDialog && (
				<AddToCartDialog
					open={showAddToCartDialog}
					onOpenChange={setShowAddToCartDialog}
					actionText="View Cart"
					secondaryActionText="Continue Shopping"
				/>
			)}
		</div>
	);
}
